import { useLingui } from '@lingui/react/macro'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { ScoreRecentResponse } from 'features/sentinels/types'
import { useCallback, useState } from 'react'

export interface ScoreRecentState {
  status: 'idle' | 'running' | 'done' | 'error'
  data?: ScoreRecentResponse
  error?: string
}

/**
 * Score a user-picked date range against a sentinel's saved models via
 * fim-service (`POST /api/v1/sites/{site}/sentinel/score`). Ephemeral: the
 * service pulls + scores in memory and returns the series; nothing is saved
 * and the view disappears on refresh.
 */
export const useScoreRecent = () => {
  const { t } = useLingui()

  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()
  const [state, setState] = useState<ScoreRecentState>({ status: 'idle' })

  const run = useCallback(
    async (
      entryId: string,
      site: string,
      startDate: string,
      endDate: string
    ) => {
      setState({ status: 'running' })
      try {
        const res = await authorizedAxios.post(
          `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(site)}/sentinel/score`,
          { entry_id: entryId, start_date: startDate, end_date: endDate }
        )
        const data = res.data as ScoreRecentResponse
        if (!data.ok) {
          setState({ status: 'error', error: data.error ?? t`scoring failed` })
          return
        }
        setState({ status: 'done', data })
      } catch (err) {
        setState({
          status: 'error',
          error: err instanceof Error ? err.message : String(err),
        })
      }
    },
    [FIM_SERVICE_URL, authorizedAxios, t]
  )

  const reset = useCallback(() => setState({ status: 'idle' }), [])

  return { state, run, reset }
}
